import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { socketService } from '../../utils/socketService';
import {
  FiSearch, FiRefreshCw, FiFilter, FiX,
  FiDollarSign, FiMessageSquare, FiUser, FiTruck,
  FiCheckCircle, FiXCircle, FiClock, FiAlertCircle,
  FiTrendingUp, FiTrendingDown, FiCalendar, FiMail, FiPhone,
  FiArrowRight, FiPackage, FiSmartphone, FiMonitor
} from 'react-icons/fi';

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Formater le montant
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'TND',
    minimumFractionDigits: 2
  }).format(amount);
};

function getChannelLabel(channel) {
  return channel === 'mobile' ? 'Application' : 'Site web';
}

function getChannelBadgeClass(channel) {
  return channel === 'mobile'
    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
    : 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40';
}

function deriveGroupStatus(offers) {
  if (!offers?.length) return 'pending';
  if (offers.some((o) => o.status === 'accepted')) return 'accepted';
  if (offers.some((o) => o.status === 'pending')) return 'pending';
  if (offers.some((o) => o.status === 'rejected')) return 'rejected';
  if (offers.some((o) => o.status === 'expired')) return 'expired';
  if (offers.some((o) => o.status === 'cancelled')) return 'cancelled';
  return offers[offers.length - 1].status || 'pending';
}

function groupNegotiationsByQuote(negotiations) {
  const map = new Map();
  for (const n of negotiations) {
    const key = n.quoteId || n.id;
    if (!map.has(key)) {
      map.set(key, {
        quoteId: key,
        quote: n.quote,
        offers: [],
        latestAt: 0,
      });
    }
    const group = map.get(key);
    group.offers.push(n);
    const t = new Date(n.createdAt || 0).getTime();
    if (t > group.latestAt) group.latestAt = t;
    if (n.quote) group.quote = n.quote;
  }
  return Array.from(map.values())
    .map((group) => {
      const offers = [...group.offers].sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      );
      const acceptedOffer = offers.find((o) => o.status === 'accepted');
      const latestOffer = offers[offers.length - 1];
      return {
        ...group,
        offers,
        aggregateStatus: deriveGroupStatus(offers),
        displayOffer: acceptedOffer || latestOffer,
        channels: [...new Set(offers.map((o) => (o.channel === 'mobile' ? 'mobile' : 'web')))],
      };
    })
    .sort((a, b) => b.latestAt - a.latestAt);
}

const EMPTY_STATS = {
  total: 0,
  pending: 0,
  accepted: 0,
  rejected: 0,
  clients: 0,
  movers: 0,
  fromMobile: 0,
  fromWeb: 0,
  averageAmount: 0,
  totalAmount: 0,
};

function AdminNegotiations() {
  const [negotiations, setNegotiations] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reloadTimerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUserType, setFilterUserType] = useState('all');
  const [filterChannel, setFilterChannel] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const loadNegotiations = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const response = await api.getAllNegotiations();
      
      if (response && response.success) {
        setNegotiations(response.data || []);
        if (response.stats) {
          setStats(response.stats);
        }
      } else {
        throw new Error(response.message || 'Erreur lors du chargement des négociations');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des négociations:', error);
      setError(error.message || 'Impossible de charger les négociations. Veuillez réessayer.');
      setNegotiations([]);
      setStats(EMPTY_STATS);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const scheduleReload = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => {
      loadNegotiations({ silent: true });
    }, 400);
  }, [loadNegotiations]);

  useEffect(() => {
    loadNegotiations();
  }, [loadNegotiations]);

  useEffect(() => {
    let unsubNegotiation;
    let unsubConnect;

    const onNegotiationUpdate = (payload) => {
      if (payload?.stats) {
        setStats(payload.stats);
      }
      scheduleReload();
    };

    const bindListeners = () => {
      unsubNegotiation = socketService.on('admin:negotiation_update', onNegotiationUpdate);
      unsubConnect = socketService.on('connect', () => {
        loadNegotiations({ silent: true });
      });
    };

    bindListeners();
    socketService.connect().then(() => {
      unsubNegotiation?.();
      unsubConnect?.();
      bindListeners();
    }).catch(() => {});

    return () => {
      unsubNegotiation?.();
      unsubConnect?.();
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    };
  }, [scheduleReload, loadNegotiations]);

  const negotiationGroups = useMemo(
    () => groupNegotiationsByQuote(negotiations),
    [negotiations]
  );

  const filteredGroups = negotiationGroups.filter((group) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      group.quote?.fromAddress?.toLowerCase().includes(searchLower) ||
      group.quote?.toAddress?.toLowerCase().includes(searchLower) ||
      group.quote?.client?.fullName?.toLowerCase().includes(searchLower) ||
      group.quote?.mover?.fullName?.toLowerCase().includes(searchLower) ||
      group.offers.some(
        (o) =>
          o.user?.fullName?.toLowerCase().includes(searchLower) ||
          o.user?.email?.toLowerCase().includes(searchLower) ||
          o.message?.toLowerCase().includes(searchLower) ||
          getChannelLabel(o.channel || 'web').toLowerCase().includes(searchLower)
      );

    const matchesStatus =
      filterStatus === 'all' ||
      group.aggregateStatus === filterStatus ||
      group.offers.some((o) => o.status === filterStatus);

    const matchesUserType =
      filterUserType === 'all' ||
      group.offers.some((o) => o.userType === filterUserType);

    const matchesChannel =
      filterChannel === 'all' ||
      group.channels.includes(filterChannel);

    return matchesSearch && matchesStatus && matchesUserType && matchesChannel;
  });

  const paginatedGroups = filteredGroups.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      expired: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      cancelled: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    return badges[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      accepted: 'Acceptée',
      rejected: 'Annulée',
      expired: 'Expirée',
      cancelled: 'Annulée'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FiClock className="w-4 h-4" />;
      case 'accepted':
        return <FiCheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <FiXCircle className="w-4 h-4" />;
      case 'expired':
        return <FiAlertCircle className="w-4 h-4" />;
      default:
        return <FiClock className="w-4 h-4" />;
    }
  };

  const getUserTypeBadge = (userType) => {
    return userType === 'client' 
      ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      : 'bg-purple-500/20 text-purple-400 border-purple-500/30';
  };

  const getUserTypeLabel = (userType) => {
    return userType === 'client' ? 'Client' : 'Déménageur';
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="space-y-6 p-6 bg-gray-900 min-h-screen text-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-yellow-600/30">
        <div>
          <h1 className="text-4xl font-extrabold text-yellow-400 mb-2 flex items-center">
            <FiMessageSquare className="w-8 h-8 mr-3" />
            Négociations de prix
          </h1>
          <p className="text-gray-400">Négociations enregistrées via le site web ou l’application mobile (même base).</p>
        </div>
        <button
          onClick={loadNegotiations}
          className="mt-4 sm:mt-0 px-5 py-2.5 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold flex items-center shadow-lg"
        >
          <FiRefreshCw className="mr-2" />
          Actualiser
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-600 rounded-lg p-4 flex items-center">
          <FiAlertCircle className="h-5 w-5 text-red-400 mr-3" />
          <p className="text-sm font-medium text-red-300 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 p-1">
            <FiX className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 shadow-xl hover:shadow-2xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-400 uppercase">Demandes</p>
            <div className="p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
              <FiMessageSquare className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
          <p className="text-4xl font-bold text-yellow-400">{negotiationGroups.length}</p>
          <p className="text-xs text-gray-500 mt-1">{stats.total} proposition(s) au total</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-600/10 to-orange-600/10 border border-yellow-600/30 rounded-xl p-6 shadow-xl hover:shadow-2xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-400 uppercase">En attente</p>
            <div className="p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
              <FiClock className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
          <p className="text-4xl font-bold text-yellow-400">{stats.pending}</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/10 to-emerald-600/10 border border-green-600/30 rounded-xl p-6 shadow-xl hover:shadow-2xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-400 uppercase">Acceptées</p>
            <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
              <FiCheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <p className="text-4xl font-bold text-green-400">{stats.accepted}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/10 to-indigo-600/10 border border-blue-600/30 rounded-xl p-6 shadow-xl hover:shadow-2xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-400 uppercase">Montant moyen</p>
            <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
              <FiDollarSign className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <p className="text-4xl font-bold text-blue-400">{formatCurrency(stats.averageAmount)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-emerald-900/20 to-gray-900 border border-emerald-600/30 rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-400 uppercase">Application mobile</p>
            <FiSmartphone className="w-6 h-6 text-emerald-400" />
          </div>
          <p className="text-3xl font-bold text-emerald-400">{stats.fromMobile}</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-900/20 to-gray-900 border border-indigo-600/30 rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-400 uppercase">Site web</p>
            <FiMonitor className="w-6 h-6 text-indigo-400" />
          </div>
          <p className="text-3xl font-bold text-indigo-400">{stats.fromWeb}</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 shadow-lg">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par utilisateur, adresse, message, canal (app / web)..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500 transition-all"
            />
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-wrap">
            <FiFilter className="text-gray-400 hidden sm:block" />
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="accepted">Acceptées</option>
              <option value="rejected">Annulées</option>
              <option value="expired">Expirées</option>
              <option value="cancelled">Annulées</option>
            </select>
            <select
              value={filterUserType}
              onChange={(e) => {
                setFilterUserType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
            >
              <option value="all">Tous les types</option>
              <option value="client">Client</option>
              <option value="mover">Déménageur</option>
            </select>
            <select
              value={filterChannel}
              onChange={(e) => {
                setFilterChannel(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
            >
              <option value="all">Tous les canaux</option>
              <option value="mobile">Application</option>
              <option value="web">Site web</option>
            </select>
          </div>
        </div>
      </div>

      {/* Negotiations List */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl overflow-hidden shadow-xl">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500"></div>
            <p className="ml-4 text-xl text-yellow-400">Chargement des négociations...</p>
          </div>
        ) : paginatedGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FiMessageSquare className="w-20 h-20 text-gray-600 mb-4" />
            <p className="text-gray-400 font-medium text-lg">Aucune négociation trouvée</p>
            <p className="text-gray-500 text-sm mt-2">Ajustez vos filtres ou attendez de nouvelles négociations</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <div className="space-y-4 p-6">
                {paginatedGroups.map((group) => {
                  const { quote, displayOffer, offers } = group;
                  const originalPrice = quote?.priceCents ? quote.priceCents / 100 : null;
                  return (
                  <div
                    key={group.quoteId}
                    className="bg-gray-900/50 border border-gray-700/50 rounded-xl p-6 hover:bg-gray-900/70 hover:border-yellow-600/30 transition-all shadow-lg"
                  >
                    <div className="flex flex-col lg:flex-row gap-6">
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                          <div>
                            <h3 className="text-lg font-bold text-white mb-1">
                              {quote?.client?.fullName || 'Client'}
                              {quote?.mover?.fullName ? (
                                <span className="text-gray-400 font-normal"> — {quote.mover.fullName}</span>
                              ) : null}
                            </h3>
                            <p className="text-sm text-gray-400">
                              {offers.length} proposition{offers.length > 1 ? 's' : ''} sur cette demande
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {group.channels.map((ch) => (
                              <span
                                key={ch}
                                className={`px-3 py-1 text-xs font-semibold rounded-full border inline-flex items-center gap-1 ${getChannelBadgeClass(ch)}`}
                              >
                                {ch === 'mobile' ? <FiSmartphone className="w-3.5 h-3.5" /> : <FiMonitor className="w-3.5 h-3.5" />}
                                {getChannelLabel(ch)}
                              </span>
                            ))}
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full border flex items-center gap-1 ${getStatusBadge(group.aggregateStatus)}`}>
                              {getStatusIcon(group.aggregateStatus)}
                              <span>{getStatusLabel(group.aggregateStatus)}</span>
                            </span>
                          </div>
                        </div>

                        {quote && (
                          <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
                            <div className="flex items-center space-x-2 mb-3">
                              <FiPackage className="w-5 h-5 text-yellow-400" />
                              <h4 className="text-sm font-semibold text-gray-300">Détails du devis</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-400 mb-1">De</p>
                                <p className="text-white font-medium">{quote.fromAddress || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-400 mb-1">À</p>
                                <p className="text-white font-medium">{quote.toAddress || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-400 mb-1">Client</p>
                                <p className="text-white font-medium">{quote.client?.fullName || 'N/A'}</p>
                              </div>
                              {quote.mover && (
                                <div>
                                  <p className="text-gray-400 mb-1">Déménageur</p>
                                  <p className="text-white font-medium">{quote.mover.fullName}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="bg-gray-800/50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-4">
                            <FiMessageSquare className="w-4 h-4 text-yellow-400" />
                            <h4 className="text-sm font-semibold text-gray-300">Historique des propositions</h4>
                          </div>
                          <div className="space-y-3">
                            {offers.map((offer, index) => (
                              <div
                                key={offer.id}
                                className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border ${
                                  offer.id === displayOffer?.id
                                    ? 'border-yellow-600/40 bg-yellow-600/5'
                                    : 'border-gray-700/50 bg-gray-900/40'
                                }`}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-700 text-xs font-bold text-gray-300 flex items-center justify-center">
                                    {index + 1}
                                  </span>
                                  <div className="w-9 h-9 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0">
                                    {getInitials(offer.user?.firstName, offer.user?.lastName)}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-sm font-semibold text-white truncate">
                                        {offer.user?.fullName || '—'}
                                      </span>
                                      <span className={`px-2 py-0.5 text-xs rounded-full border ${getUserTypeBadge(offer.userType)}`}>
                                        {getUserTypeLabel(offer.userType)}
                                      </span>
                                      <span className={`px-2 py-0.5 text-xs rounded-full border inline-flex items-center gap-1 ${getStatusBadge(offer.status)}`}>
                                        {getStatusIcon(offer.status)}
                                        {getStatusLabel(offer.status)}
                                      </span>
                                    </div>
                                    {offer.message && (
                                      <p className="text-xs text-gray-400 mt-1">{offer.message}</p>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">{formatDate(offer.createdAt)}</p>
                                  </div>
                                </div>
                                <p className="text-lg font-bold text-yellow-400 sm:text-right shrink-0">
                                  {formatCurrency(offer.amount)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="lg:w-64 flex flex-col justify-between shrink-0">
                        {displayOffer && (
                          <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 border border-yellow-600/30 rounded-lg p-4 mb-4">
                            <p className="text-sm text-gray-400 mb-2">
                              {displayOffer.status === 'accepted' ? 'Prix retenu' : 'Dernière proposition'}
                            </p>
                            <p className="text-3xl font-bold text-yellow-400 mb-1">
                              {formatCurrency(displayOffer.amount)}
                            </p>
                            {originalPrice != null && originalPrice > 0 && (
                              <div className="flex items-center space-x-2 mt-2">
                                {displayOffer.amount < originalPrice ? (
                                  <span className="flex items-center text-xs text-green-400">
                                    <FiTrendingDown className="w-3 h-3 mr-1" />
                                    -{formatCurrency(originalPrice - displayOffer.amount)}
                                  </span>
                                ) : displayOffer.amount > originalPrice ? (
                                  <span className="flex items-center text-xs text-red-400">
                                    <FiTrendingUp className="w-3 h-3 mr-1" />
                                    +{formatCurrency(displayOffer.amount - originalPrice)}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">Prix original</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {group.quoteId && (
                          <Link
                            to={`/admin/moves/${group.quoteId}`}
                            className="flex items-center justify-center px-4 py-2 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 rounded-lg border border-yellow-600/30 transition-all font-medium"
                          >
                            Voir le devis
                            <FiArrowRight className="w-4 h-4 ml-2" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}

              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-900/50 px-6 py-4 flex items-center justify-between border-t border-gray-700">
                <div className="text-sm text-gray-400">
                  Affichage de <span className="font-semibold text-white">{(currentPage - 1) * itemsPerPage + 1}</span> à{' '}
                  <span className="font-semibold text-white">{Math.min(currentPage * itemsPerPage, filteredGroups.length)}</span> sur{' '}
                  <span className="font-semibold text-white">{filteredGroups.length}</span> demandes
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm font-medium bg-gray-800 hover:bg-gray-700 hover:border-yellow-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Précédent
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-400">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm font-medium bg-gray-800 hover:bg-gray-700 hover:border-yellow-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminNegotiations;


